import React, { useMemo } from 'react';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import Loader from '@components-clean/core/loader';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';
import { SubjectNotesApi } from '@apis/bff-api/subject-monitoring/subject-notes-api';

const { TooltipCell } = createGridCustomCells();

const defaultColumnSize = { width: 1, minWidth: 140 };

type Props = {
    studyRef: string;
    // PgId, not recordId
    reviewId: number;
};

const SMSelectedReviewSectionNotesTab = ({ studyRef, reviewId }: Props) => {
    const { t: tCommon } = useTranslation('common');
    const { t } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit.manageReviewsGrid.selectedReviewSection.tabs.notes',
    });

    const getAllReviewNotesQuery = SubjectNotesApi.useGetAllNotesForReview(studyRef, reviewId);
    const getActiveDatasetsQuery = DatasetApi.useGetActive(studyRef);

    const isLoading = getAllReviewNotesQuery.isLoading || getActiveDatasetsQuery.isLoading;
    const isError = !!getAllReviewNotesQuery.error || !!getActiveDatasetsQuery.error;

    const data = useMemo(() => {
        return (getAllReviewNotesQuery.data ?? [])
            .toSorted((a, z) => Number(z.recordId) - Number(a.recordId))
            .toSorted(
                (a, z) =>
                    new Date(z.observationDate as string).getTime() - new Date(a.observationDate as string).getTime(),
            );
    }, [getAllReviewNotesQuery.data]);

    const columns = useMemo<GridColumnProps[]>(() => {
        const datasetDates = [
            ...new Set(
                getActiveDatasetsQuery.data?.map((x) => stringDateWithoutTimeFromUtc(x.dataCutDate!, 'DD-MMM-YYYY')!),
            ),
        ].sort((a, b) => (new Date(a) > new Date(b) ? -1 : 1));

        if (datasetDates.length === 0) {
            return [];
        }

        const tooltipCellTwoLine = TooltipCell({
            isCsv: false,
            maxLines: 2,
        });

        const noteColumns: GridColumnProps[] = [
            {
                id: 'subject',
                field: 'subjectNumber',
                title: t('grid.fields.subjectNumber'),
                showByDefault: true,
                ...defaultColumnSize,
            },
            {
                id: 'noteText',
                field: 'noteText',
                title: t('grid.fields.noteText'),
                width: 1,
                minWidth: 250,
                lockedByDefault: true,
                cells: { data: tooltipCellTwoLine },
            },
            {
                id: 'observationDate',
                field: 'observationDate',
                title: t('grid.fields.observationDate'),
                cells: { data: DateCell },
                lockedByDefault: true,
                filter: 'date',
                ...defaultColumnSize,
            },
            {
                id: 'datasetOpenedOn',
                field: 'datasetOpenedOn',
                title: t('grid.fields.datasetOpenedOn'),
                lovData: datasetDates,
                ...defaultColumnSize,
            },
            {
                id: 'association',
                field: 'association',
                title: t('grid.fields.association'),
                lovData: ['Subject Hub', 'Subject Profile'],
                ...defaultColumnSize,
            },
            {
                id: 'dataListing',
                field: 'dataListing',
                title: t('grid.fields.dataListing'),
                ...defaultColumnSize,
            },
            {
                id: 'createdByName',
                field: 'createdByName',
                title: t('grid.fields.createdByName'),
                cells: { data: tooltipCellTwoLine },
                ...defaultColumnSize,
            },
            {
                id: 'created',
                field: 'created',
                title: t('grid.fields.created'),
                cells: { data: DateCell },
                filter: 'date',
                ...defaultColumnSize,
            },
            {
                id: 'updatedByName',
                field: 'updatedByName',
                title: t('grid.fields.updatedByName'),
                cells: { data: tooltipCellTwoLine },
                ...defaultColumnSize,
            },
            {
                id: 'updatedDate',
                field: 'updatedDate',
                title: t('grid.fields.updatedDate'),
                cells: { data: DateCell },
                filter: 'date',
                ...defaultColumnSize,
            },
            {
                id: 'observationId',
                field: 'id',
                title: t('grid.fields.observationId'),
                showByDefault: false,
                ...defaultColumnSize,
            },
        ];

        return noteColumns;
    }, [getActiveDatasetsQuery.data, t]);

    return (
        <>
            {isLoading || isError ? (
                <div>
                    {isError ? (
                        <InfoField type="Error" message={tCommon('errorMessage')} />
                    ) : isLoading ? (
                        <Loader />
                    ) : null}
                </div>
            ) : (
                <Grid
                    title={t('grid.title')}
                    testDataId="selected-review-notes-grid"
                    columns={columns}
                    data={data}
                    maxHeight="auto"
                    gridDownloadDisabled
                    showAddIcon={false}
                    canEdit={false}
                />
            )}
        </>
    );
};

export default SMSelectedReviewSectionNotesTab;
